import api from "../config/api";

import {
  ServicePaginatedReturn,
  ServiceReturn,
} from "../types/ServiceResponse";

import { Service, ServiceStatus } from "../models/Service";
import { Message } from "../models/Message";
import { FileType } from "../types/FileType";
import { UploadedFileReference } from "./uploadServices";
import { normalizeMessage, normalizeService } from "./messageAdapters";

export enum Order {
  ASC = "ASC",
  DESC = "DESC",
};

export interface GetServicesPayload {
  status: ServiceStatus;
  order?: Order;
  page?: number;
  perPage?: number;
  search?: string;
  queueId?: string;
  channelId?: string;
};

export interface DefaultServicePayload {
  id: string;
  comment?: string;
};

export interface SendMessagePayload {
  id?: string;
  message: string;
  internal?: boolean;
  attachment?: File | null;
  attachmentType?: FileType | null;
  fileMetadata?: UploadedFileReference | null;
  replyToMessageId?: string | null;
  onUploadProgress?: ( percentage: number ) => void;
};

export interface ReactMessagePayload {
  id: string;
  messageId: string;
  emoji: string;
};

export interface EditMessagePayload {
  id: string;
  messageId: string;
  message: string;
};

export interface DeleteMessagePayload {
  id: string;
  messageId: string;
};

export interface RetryMessagePayload {
  serviceId: string;
  messageId: string;
};

export interface StartConversationPayload {
  channelId?: string;
  name: string;
  phone: string;
  queueId?: string;
};

export interface TransferServicePayload extends DefaultServicePayload {
  queueId?: string;
};

export interface GetTotalWaiting {
  search: string;
  queueId?: string;
  channelId?: string;
};

const moduleBaseUrl = "service";

export const getServices = async ( params: GetServicesPayload, token?: string ): Promise<ServicePaginatedReturn<Service>> => {
  try {
    const {
      data
    } = await api.get(`/${ moduleBaseUrl }`, {
      headers: {
        Authorization: `Bearer ${ token }`,
      },
      params,
    });

    if( data?.data?.results ) {
      data.data.results = data.data.results.map( normalizeService );
    }

    return data;
  } catch ( error: any ) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};

export const getTotalWaiting = async ( params: GetTotalWaiting, token?: string ): Promise<ServiceReturn<number>> => {
  try {
    const {
      data
    } = await api.get(`/${ moduleBaseUrl }/total-waiting`, {
      headers: {
        Authorization: `Bearer ${ token }`,
      },
      params,
    });

    return data;
  } catch ( error: any ) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};

export const sendMessage = async ({ message, id, internal, attachment, attachmentType, fileMetadata, replyToMessageId, onUploadProgress }: SendMessagePayload, token?: string ): Promise<ServiceReturn<Message>> => {
  try {
    const shouldUseMetadataOnly = Boolean(fileMetadata) && !attachment && Boolean(attachmentType);

    const { data } = shouldUseMetadataOnly
      ? await api.post(`/${ moduleBaseUrl }/${ id }/send-message`, {
          message,
          internal,
          attachmentType,
          replyToMessageId,
          fileId: fileMetadata?.fileId,
          storageKey: fileMetadata?.storageKey,
          thumbnailKey: fileMetadata?.thumbnailKey,
          mimeType: fileMetadata?.mimeType,
          fileName: fileMetadata?.fileName,
          originalFileName: fileMetadata?.originalFileName,
          size: fileMetadata?.size,
          extension: fileMetadata?.extension,
          sha256: fileMetadata?.sha256,
          durationSeconds: fileMetadata?.durationSeconds,
          pageCount: fileMetadata?.pageCount,
          width: fileMetadata?.width,
          height: fileMetadata?.height,
          uploadStatus: fileMetadata?.uploadStatus ?? "COMPLETED",
        }, {
          headers: {
            Authorization: `Bearer ${ token }`,
          }
        })
      : await (() => {
          const payload = new FormData();

          payload.append("message", message );
          if( internal ) payload.append("internal", "true" );
          if( replyToMessageId ) payload.append("replyToMessageId", replyToMessageId );

          if( attachmentType && attachment ) {
            payload.append("attachmentType", attachmentType );
            payload.append("attachment", attachment );
          }

          return api.post(`/${ moduleBaseUrl }/${ id }/send-message`, payload, {
            headers: {
              Authorization: `Bearer ${ token }`,
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: ( event ) => {
              if( !onUploadProgress || !event.total ) return;
              onUploadProgress( Math.min( 100, Math.round(( event.loaded / event.total ) * 100 )));
            },
          });
        })();

    if( data?.data ) {
      data.data = normalizeMessage( data.data );
    }

    return data;
  } catch ( error: any ) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};

export const reactMessage = async ({ id, messageId, emoji }: ReactMessagePayload, token?: string ): Promise<ServiceReturn<Message>> => {
  try {
    const { data } = await api.post(`/${ moduleBaseUrl }/${ id }/react-message`, {
      messageId,
      emoji,
    }, {
      headers: {
        Authorization: `Bearer ${ token }`,
      }
    });

    if( data?.data ) data.data = normalizeMessage( data.data );

    return data;
  } catch ( error: any ) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};

export const editMessage = async ({ id, messageId, message }: EditMessagePayload, token?: string ): Promise<ServiceReturn<Message>> => {
  try {
    const { data } = await api.patch(`/${ moduleBaseUrl }/${ id }/message/${ messageId }`, {
      message,
    }, {
      headers: {
        Authorization: `Bearer ${ token }`,
      }
    });

    if( data?.data ) data.data = normalizeMessage( data.data );

    return data;
  } catch ( error: any ) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};

export const deleteMessage = async ({ id, messageId }: DeleteMessagePayload, token?: string ): Promise<ServiceReturn<Message>> => {
  try {
    const { data } = await api.delete(`/${ moduleBaseUrl }/${ id }/message/${ messageId }`, {
      headers: {
        Authorization: `Bearer ${ token }`,
      }
    });

    if( data?.data ) data.data = normalizeMessage( data.data );

    return data;
  } catch ( error: any ) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};

export const retryMessage = async ({ serviceId, messageId }: RetryMessagePayload, token?: string ): Promise<ServiceReturn<Message>> => {
  try {
    const { data } = await api.post(`/${ moduleBaseUrl }/${ serviceId }/message/${ messageId }/retry`, {}, {
      headers: { Authorization: `Bearer ${ token }` },
    });

    if( data?.data ) data.data = normalizeMessage( data.data );

    return data;
  } catch ( error: any ) {
    return { data: false, message: error?.response?.data?.message ?? error?.message };
  }
};

export const startConversation = async ( payload: StartConversationPayload, token?: string ): Promise<ServiceReturn<Service>> => {
  try {
    const {
      data
    } = await api.post(`/${ moduleBaseUrl }/start-conversation`, payload, {
      headers: {
        Authorization: `Bearer ${ token }`,
      }
    });

    if( data?.data ) {
      data.data = normalizeService( data.data );
    }

    return data;
  } catch ( error: any ) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};

export const transferService = async ({ id, queueId, comment }: TransferServicePayload, token?: string ): Promise<ServiceReturn<boolean>> => {
  try {
    const {
      data
    } = await api.patch(`/${ moduleBaseUrl }/${ id }/transfer`, { queueId, comment }, {
      headers: {
        Authorization: `Bearer ${ token }`,
      }
    });

    return data;
  } catch ( error: any ) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};

export const abandonService = async ({ id }: DefaultServicePayload, token?: string ): Promise<ServiceReturn<boolean>> => {
  try {
    const {
      data
    } = await api.patch(`/${ moduleBaseUrl }/${ id }/abandon`, {}, {
      headers: {
        Authorization: `Bearer ${ token }`,
      }
    });

    return data;
  } catch ( error: any ) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};

export const closeService = async ({ id, comment }: DefaultServicePayload, token?: string ): Promise<ServiceReturn<boolean>> => {
  try {
    const {
      data
    } = await api.delete(`/${ moduleBaseUrl }/${ id }/close`, {
      headers: {
        Authorization: `Bearer ${ token }`,
      },
      data: { comment },
    });

    return data;
  } catch ( error: any ) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};
